import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { doc, onSnapshot, updateDoc } from 'firebase/firestore';
import { auth, db } from '../firebase';
import './AccountBilling.css';

const AccountBilling = () => {
  const [loading, setLoading] = useState(true);
  const [editable, setEditable] = useState(false);
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    businessName: '',
    plan: '',
    role: '',
    tenantId: null // store tenantId string for ref doc
  });

  // Fetch user and tenant data in real-time
  useEffect(() => {
    if (!auth.currentUser) return;

    const userRef = doc(db, 'users', auth.currentUser.uid);

    const unsubscribe = onSnapshot(userRef, (docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data();
        
        // Split name (safely fallback)
        const fullName = data.name || '';
        const nameParts = fullName.split(' ');
        const fName = nameParts[0] || '';
        const lName = nameParts.length > 1 ? nameParts.slice(1).join(' ') : '';
        
        setFormData({
          firstName: fName,
          lastName: lName,
          email: data.email || '',
          businessName: '', // will be populated if tenant exists
          plan: data.plan || 'Free',
          role: data.role || 'User',
          tenantId: data.tenantId || null
        });

        // Also fetch tenant doc if tenantId exists on user
        if (data.tenantId) {
            onSnapshot(doc(db, 'tenants', data.tenantId), (tenantSnap) => {
                if (tenantSnap.exists()) {
                    setFormData(prev => ({
                        ...prev,
                        businessName: tenantSnap.data().tenantName || ''
                    }));
                }
            });
        }
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      const userRef = doc(db, 'users', auth.currentUser.uid);
      
      const combinedName = `${formData.firstName} ${formData.lastName}`.trim();

      // Update basic fields on user doc
      await updateDoc(userRef, {
        name: combinedName
      });

      // Update business name on tenant doc
      if (formData.tenantId) {
         const tenantRef = doc(db, 'tenants', formData.tenantId);
         await updateDoc(tenantRef, {
             tenantName: formData.businessName
         });
      }

      setEditable(false);
      alert('Changes saved successfully!');
    } catch (error) {
      console.error('Error saving data:', error);
      alert('Failed to save changes.');
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div className="loading-spinner">Loading...</div>;

  return (
    <div className="account-billing-page">
      <div className="account-card">
        <button className="back-btn" onClick={() => navigate('/dashboard')}>
          &larr; Back to Dashboard
        </button>
        
        <h1 className="account-title">Account Information</h1>
        <p className="account-subtitle">Manage your personal and business details here.</p>

        <div className="form-grid">
          <div className="form-group half-width">
            <label>First Name</label>
            <input
              type="text"
              name="firstName"
              value={formData.firstName}
              onChange={handleInputChange}
              disabled={!editable}
            />
          </div>
          <div className="form-group half-width">
            <label>Last Name</label>
            <input
              type="text"
              name="lastName"
              value={formData.lastName}
              onChange={handleInputChange}
              disabled={!editable}
            />
          </div>
          
          <div className="form-group full-width">
            <label>Email Address</label>
            <input type="email" value={formData.email} disabled className="disabled-input" />
          </div>

          <div className="form-group full-width">
            <label>Business / Clinic Name</label>
            <input
              type="text"
              name="businessName"
              value={formData.businessName}
              onChange={handleInputChange}
              disabled={!editable}
            />
          </div>

          <div className="form-group half-width">
            <label>Current Plan</label>
            <input type="text" value={formData.plan} disabled className="disabled-input" />
          </div>
          
          <div className="form-group half-width">
            <label>Role</label>
            <input type="text" value={formData.role} disabled className="disabled-input" />
          </div>
        </div>

        <div className="action-buttons">
          {!editable ? (
            <button className="edit-btn" onClick={() => setEditable(true)}>
              Edit Information
            </button>
          ) : (
            <>
              <button className="cancel-btn" onClick={() => setEditable(false)}>
                Cancel
              </button>
              <button className="save-btn" onClick={handleSave} disabled={loading}>
                {loading ? 'Saving...' : 'Save Changes'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AccountBilling;
